/**
 * HEAREN V1.1.1 - GUIDED SESSION SERVICE
 * 
 * Reuses existing Radio voice messages (41 phrases) to power Presence sessions.
 * Plays Soundscapes bed with voice overlays every 60 seconds.
 * 
 * Features:
 * - Fetches voice messages from Admin (same dataset as Radio)
 * - Filters by language (EN/FR) and enabled=true
 * - Preserves original order
 * - Music ducking during voice (0.55 gain, then 1.0)
 * - No voice stacking (skips if voice still playing)
 * - V1.1.1: User-adjustable voice volume (default 30%)
 * 
 * Audio chain: voiceSource → voiceProgramGain → voiceUserGain → masterGain
 */
import { nativeAudioPlayer } from '@/native/nativeAudio/nativeAudioPlayer';
import { API_BASE_URL } from './httpClient';

// Storage key for voice volume
const VOICE_VOLUME_KEY = 'hearen.voiceVolume';

// Default voice volume (0.40 = 40%)
const DEFAULT_VOICE_VOLUME = 0.48;

class PresenceService {
  constructor() {
    // V1.1.1: Uses shared AudioContext from audioService (no separate context)
    this.audioContext = null;
    this.graph = null; // Reference to audioService.ensureAudioGraph()
    
    // Voice state
    this.voiceMessages = [];
    this.voiceIndex = 0;
    this.voiceTimer = null;
    this.voiceSource = null;
    this.isVoicePlaying = false;
    
    // Session state
    this.isActive = false;
    this.isPaused = false;
    this.lang = 'fr';
    this.theme = null; // Current theme (e.g. 'nature', 'sleep')
    this.userId = null;
    this._sessionStartPlayed = false; // Welcome message guard
    this._lastPlayedMessageId = null; // Anti-repeat for during_session
    
    // Transition state
    this._trackCount = 0; // Tracks played in this session
    this._tracksSinceTransition = 0; // Tracks since last transition message
    this._recentTransitionIds = []; // Last 5 transition message IDs
    this._transitionEveryNTracks = 2; // Default: play transition every 2 tracks (configurable via admin)
    this._transitionLock = false; // Mutex to prevent concurrent tryTransitionMessage calls
    
    // Settings
    this.voiceInterval = 180000; // Legacy — unused, kept for rollback compatibility
    this.musicDuckLevel = 0.15; // Reduce music to 15% during voice
    this.voiceBoost = 6.0; // Boost voice gain (ElevenLabs TTS files are quieter than music)
    
    // V1.1.1: User voice volume (0.0 - 1.0)
    this.userVoiceVolume = this._loadVoiceVolume();
    
    // Callbacks
    this.onVoiceStart = null;
    this.onVoiceEnd = null;
    this.onStateChange = null;
    this.onVolumeChange = null;
    
    console.log(`[PresenceService] Initialized (voiceVolume=${this.userVoiceVolume})`);
  }
  
  /**
   * Load voice volume from localStorage
   * @returns {number} Voice volume (0.0 - 1.0)
   */
  _loadVoiceVolume() {
    try {
      const saved = localStorage.getItem(VOICE_VOLUME_KEY);
      if (saved !== null) {
        const value = parseFloat(saved);
        if (!isNaN(value) && value >= 0 && value <= 1) {
          console.log(`[PresenceService] Loaded voice volume: ${value}`);
          return value;
        }
      }
    } catch (e) {
      console.error('[PresenceService] Error loading voice volume:', e);
    }
    // First run - use default
    console.log(`[PresenceService] Using default voice volume: ${DEFAULT_VOICE_VOLUME}`);
    return DEFAULT_VOICE_VOLUME;
  }
  
  /**
   * Save voice volume to localStorage
   * @param {number} value - Voice volume (0.0 - 1.0)
   */
  _saveVoiceVolume(value) {
    try {
      localStorage.setItem(VOICE_VOLUME_KEY, value.toString());
    } catch (e) {
      console.error('[PresenceService] Error saving voice volume:', e);
    }
  }
  
  /**
   * Set voice volume (0.0 - 1.0)
   * @param {number} value - Voice volume
   */
  setVoiceVolume(value) {
    const clampedValue = Math.max(0, Math.min(1, value));
    this.userVoiceVolume = clampedValue;
    this._saveVoiceVolume(clampedValue);
    
    // Apply to shared voiceUserGain node
    if (this.graph && this.audioContext) {
      const t = this.audioContext.currentTime;
      const boostedValue = clampedValue * this.voiceBoost;
      this.graph.voiceUserGain.gain.cancelScheduledValues(t);
      this.graph.voiceUserGain.gain.setValueAtTime(this.graph.voiceUserGain.gain.value, t);
      this.graph.voiceUserGain.gain.setTargetAtTime(boostedValue, t, 0.02);
    }
    
    if (this.onVolumeChange) {
      this.onVolumeChange(clampedValue);
    }
    
    console.log(`[PresenceService] Voice volume set to ${Math.round(clampedValue * 100)}% (gain=${(clampedValue * this.voiceBoost).toFixed(2)})`);
  }
  
  /**
   * Get current voice volume (0.0 - 1.0)
   * @returns {number}
   */
  getVoiceVolume() {
    return this.userVoiceVolume;
  }
  
  /**
   * Initialize audio context using the SHARED audioService graph.
   * No separate AudioContext — everything on one graph.
   */
  async initAudioContext() {
    if (this.audioContext) return;
    
    try {
      // Import audioService to get shared context
      const { default: audioService } = await import('./audioService');
      audioService.init();
      
      this.audioContext = audioService.audioContext;
      this.graph = audioService.ensureAudioGraph();

      // Ensure shared master gain is open for Hera voice
      if (this.graph?.masterGain && this.graph.masterGain.gain.value === 0) {
        this.graph.masterGain.gain.setValueAtTime(1.0, this.audioContext.currentTime);
        console.log('[PresenceService] masterGain opened for voice');
      }
      
      // Apply initial voice volume to the shared voiceUserGain node
      const t = this.audioContext.currentTime;
      this.graph.voiceUserGain.gain.cancelScheduledValues(t);
      this.graph.voiceUserGain.gain.setValueAtTime(this.userVoiceVolume * this.voiceBoost, t);
      
      console.log(`[PresenceService] Using SHARED AudioContext (id=${this.audioContext.constructor.name})`);
      console.log(`[PresenceService] Voice chain: voiceProgramGain → voiceUserGain → masterGain → destination`);
      console.log(`[PresenceService] voiceUserGain.value = ${this.userVoiceVolume} × ${this.voiceBoost} = ${this.userVoiceVolume * this.voiceBoost}`);
    } catch (error) {
      console.error('[PresenceService] Failed to initialize audio context:', error);
      throw error;
    }
  }
  
  /**
   * Load voice messages from backend
   * @param {string} lang - Language code (fr/en)
   */
  async loadVoiceMessages(lang = 'fr', theme = null) {
    this.lang = lang;
    if (theme) this.theme = theme;
    
    try {
      console.log(`[PresenceService] Loading voice messages (lang=${lang}, theme=${this.theme})`);
      
      // Fetch from presence endpoint (reuses radio voice messages)
      let url = `${API_BASE_URL}/api/guided/voice-messages?lang=${lang}`;
      if (this.theme) url += `&theme=${this.theme}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      this.voiceMessages = data.messages || [];
      this.voiceIndex = 0;
      
      console.log(`[PresenceService] Loaded ${this.voiceMessages.length} voice messages`);
      
      return this.voiceMessages.length > 0;
    } catch (error) {
      console.error('[PresenceService] Failed to load voice messages:', error);
      this.voiceMessages = [];
      return false;
    }
  }
  
  /**
   * Start presence session
   * Call this after Soundscapes music is already playing
   * @param {Object} options - { lang, musicGainNode }
   */
  async start(options = {}) {
    const { lang = 'fr', theme = null, userId = null } = options;
    
    const themeChanged = theme && theme !== this.theme;
    if (theme) this.theme = theme;
    if (userId) this.userId = userId;
    
    console.log(`[PresenceService] Starting presence session (theme=${this.theme}, userId=${this.userId}, themeChanged=${themeChanged})`);
    
    await this.initAudioContext();
    
    // Fetch voice transition config for this theme
    if (themeChanged || !this._transitionEveryNTracks) {
      await this._fetchVoiceConfig();
    }
    
    // Reload voice messages if theme changed or not yet loaded
    if (this.voiceMessages.length === 0 || this.lang !== lang || themeChanged) {
      await this.loadVoiceMessages(lang, this.theme);
    }
    
    if (this.voiceMessages.length === 0) {
      console.warn('[PresenceService] No voice messages available');
    }
    
    const wasActive = this.isActive;
    this.isActive = true;
    this.isPaused = false;
    
    // Reset transition counters for fresh session
    // (stop() also resets these, but guard against start() without prior stop())
    if (!wasActive) {
      this._trackCount = 0;
      this._tracksSinceTransition = 0;
      this._transitionLock = false;
    }
    
    // Reset welcome guard if theme changed
    if (themeChanged) {
      this._sessionStartPlayed = false;
    }
    
    // TEST MODE — force welcome on every theme start
    this._sessionStartPlayed = false;

    console.warn('[PresenceService][WELCOME GUARD]', {
      sessionStartPlayed: this._sessionStartPlayed,
      theme: this.theme,
      shouldPlay: !this._sessionStartPlayed && !!this.theme,
    });

    // Play welcome message (session_start) if not already played
    if (!this._sessionStartPlayed && this.theme) {
      console.warn('[PresenceService][WELCOME CALL] about to call _playWelcomeMessage');

      this._playWelcomeMessage()
        .then(() => {
          console.warn('[PresenceService][WELCOME CALL] resolved');
        })
        .catch((err) => {
          console.error('[PresenceService][WELCOME CALL] error', {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
          });
        });
    }
    
    // V1.4.0: Periodic during_session messages DISABLED.
    // Only welcome (session_start) and transition messages are active.
    // Kept in place for easy rollback — uncomment to re-enable.
    // this.scheduleNextVoice();
    
    this.notifyStateChange('started');
    
    return true;
  }
  
  /**
   * Play welcome/session_start message via /api/voice-messages/pick
   */
  async _playWelcomeMessage() {
    console.log('[PresenceService][WELCOME] forced test mode');
    this._sessionStartPlayed = false; 
    
    try {
      const voice = 'hera';
      const theme = this.theme;
      const userId = this.userId || 'anonymous';

      console.log('[PresenceService][WELCOME][THEME CHECK]', {
        theme,
        lang: this.lang,
        userId: this.userId,
      });
      
      // Anti-repeat: get last welcome message ID from localStorage
      const lastWelcomeKey = `presence_last_welcome_${theme}_${this.lang}`;
      const lastWelcomeId = localStorage.getItem(lastWelcomeKey) || '';
      
      console.log(`[PresenceService][WELCOME] Picking session_start for theme=${theme} (lastId=${lastWelcomeId || 'none'})`);
      
      const params = new URLSearchParams({
        trigger_mode: 'session_start',
        theme, lang: this.lang, voice, user_id: userId
      });

      if (lastWelcomeId) {
        params.set('last_message_id', lastWelcomeId);
      }
      
      const response = await fetch(`${API_BASE_URL}/api/voice-messages/pick?${params}`);
      
      if (response.status === 204) {
        console.log('[PresenceService][WELCOME] No welcome message (204)');
        return;
      }
      
      if (!response.ok) {
        console.warn(`[PresenceService][WELCOME] Error: HTTP ${response.status}`);
        return;
      }
      
      const data = await response.json();
      console.log(`[PresenceService][WELCOME] Picked: ${data.message_id} source=${data.source}`);
      
      // Persist for anti-repeat across sessions
      try { localStorage.setItem(lastWelcomeKey, data.message_id); } catch (e) {}
      
      if (!data.file_url) {
        console.log('[PresenceService][WELCOME] No audio URL');
        return;
      }
      
      // Play after short delay (let music settle)
      setTimeout(async () => {
        console.warn('[PresenceService][WELCOME TIMEOUT FIRED]', {
          isActive: this.isActive,
          dataFileUrl: data.file_url,
          messageId: data.message_id,
        });

  if (!this.isActive) return;

  try {
          // Use stream=true to proxy audio through backend (avoids CORS on R2 redirect)
          const streamUrl = data.file_url.includes('?')
           ? `${data.file_url}&stream=true`
           : `${data.file_url}?stream=true`;
          const msg = { id: data.message_id, text: data.text, audio_url: streamUrl };
          await this.playVoiceMessage(msg);
          
          // Mark as played
          if (this.userId) {
            fetch(`${API_BASE_URL}/api/radio/mark-played?user_id=${this.userId}&message_id=${data.message_id}`, { method: 'POST' }).catch(() => {});
          }
        } catch (err) {
          console.error('[PresenceService][WELCOME] Playback error:', {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
          });
        }
      }, 500);
      
    } catch (error) {
      console.error('[PresenceService][WELCOME] Error:', error);
    }
  }

  /**
   * Fetch voice transition config for current theme.
   * Only reads transition_every_n_tracks.
   */
  async _fetchVoiceConfig() {
    if (!this.theme) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/voice-profile/${this.theme}`);
      if (response.ok) {
        const data = await response.json();
        this._transitionEveryNTracks = 1;
        console.log(`[PresenceService] Voice config: transitionEvery=${this._transitionEveryNTracks} tracks`);
      } else {
        this._transitionEveryNTracks = 1;
        console.warn(`[PresenceService] Voice config fetch failed (${response.status}), using default=2`);
      }
    } catch (error) {
      this._transitionEveryNTracks = 1;
      console.warn('[PresenceService] Voice config fetch error:', error.message);
    }
  }


  // ==================== TRANSITION MESSAGES ====================

  /**
   * Called by TherapyPlayer when a track ends and a new one is about to start.
   * Returns a Promise that resolves when the transition message finishes (or immediately if none).
   * Uses a mutex to prevent concurrent calls from causing voice overlap.
   */
  async tryTransitionMessage() {
    console.log('[HERA][TRY TRANSITION][ENTER]', {
      trackCount: this._trackCount,
      lastTransitionTrack: this._lastTransitionTrack,
      lastTransitionAt: this._lastTransitionAt,
      isVoicePlaying: this.isVoicePlaying,
      isActive: this.isActive
    });
    // Guard: don't trigger if service is stopped or paused
    if (!this.isActive || this.isPaused) {
      console.log(`[Transition] Skipped — isActive=${this.isActive}, isPaused=${this.isPaused}`);
      return false;
    }

    // Mutex: prevent concurrent calls
    if (this._transitionLock) {
      console.log('[Transition] Skipped — transition lock active');
      return false;
    }
    if (this.isVoicePlaying && this.voiceStartedAt) {
      const elapsed = Date.now() - this.voiceStartedAt;

      if (elapsed > 45000) {
        console.warn('[PresenceService] STALE VOICE DETECTED', { elapsed });
        this.resetVoiceState('stale watchdog');
      }
    }
    // HEAREN P0 — stale voice watchdog
    if (this.isVoicePlaying && this.voiceStartedAt) {
      const elapsed = Date.now() - this.voiceStartedAt;

      if (elapsed > 45000) {
        console.warn('[Transition] STALE VOICE DETECTED', {
          elapsed
        });

        this.resetVoiceState('transition watchdog');
      }
    }

    // Skip if voice already playing
    if (this.isVoicePlaying) {
      console.log('[Transition] Voice already playing, skipping');
      return false;
    }

    this._trackCount++;
    this._tracksSinceTransition++;

    // TEST MODE : message Hera à chaque transition
    const n = 1;
    

    // Rule: play a transition after every N tracks
    if (this._tracksSinceTransition < n) {
      console.log(`[Transition] ${this._tracksSinceTransition}/${n} tracks since last transition, waiting...`);
      return false;
    }

    // V1.4.0: Transitions are the ONLY voice between welcome and end of session.
    // Probability is 100% once eligible (past track spacing rule).

    // Acquire lock BEFORE async operations
    this._transitionLock = true;

    // Try to pick a transition message from backend
    try {
      const theme = this.theme || 'generic';
      const userId = this.userId || 'anonymous';
      const params = new URLSearchParams({
        trigger_mode: 'transition',
        theme,
        lang: this.lang,
        voice: 'hera',
        user_id: userId
      });

      // Rule 3: Exclude recent transition IDs
      if (this._recentTransitionIds.length > 0) {
        params.set('last_message_id', this._recentTransitionIds[this._recentTransitionIds.length - 1]);
      }

      console.log(`[Transition] Picking message (track=${this._trackCount}, sinceTransition=${this._tracksSinceTransition}/${n})`);

      console.log('[Transition][REQUEST]', {
        theme,
        lang: this.lang,
        url: `${API_BASE_URL}/api/voice-messages/pick?${params}`,
      });

      const response = await fetch(`${API_BASE_URL}/api/voice-messages/pick?${params}`);

      if (response.status === 204 || !response.ok) {
        console.log('[Transition] No eligible message found — continuing to next track');
        this._transitionLock = false;
        return false;
      }

      const data = await response.json();

      // Check recent history (anti-repeat)
      //if (this._recentTransitionIds.includes(data.message_id)) {
        //console.log(`[Transition] Message ${data.message_id.substring(0, 8)} in recent history, skipping`);
        //this._transitionLock = false;
        //return false;
      //}

      if (!data.file_url) {
        this._transitionLock = false;
        return false;
      }

      console.log(`[Transition] Playing: ${data.message_id.substring(0, 8)}... "${data.text?.substring(0, 40)}..."`);

      // Play the transition message (blocks until done)
      const streamUrl = data.file_url.includes('?')
        ? `${data.file_url}&stream=true`
        : `${data.file_url}?stream=true`;
      const msg = { id: data.message_id, text: data.text, audio_url: streamUrl };

      await this.playVoiceMessage(msg);

      console.log('[Transition] playVoiceMessage resolved — unlocking transition');

      // Update state after successful play
      this._recentTransitionIds.push(data.message_id);
      if (this._recentTransitionIds.length > 5) this._recentTransitionIds.shift();
      this._tracksSinceTransition = 0;

      // Mark as played
      if (this.userId) {
        fetch(`${API_BASE_URL}/api/radio/mark-played?user_id=${this.userId}&message_id=${data.message_id}`, { method: 'POST' }).catch(() => {});
      }

      this._transitionLock = false;
      return true;
    } catch (error) {
      console.error('[Transition] Error:', error);
      this._transitionLock = false;
      return false;
    }
  }

  
  /**
 * Stop presence session
 */
stop() {
  if (window.__HEAREN_KEEP_PRESENCE === true) {
    console.log('[PresenceService] stop skipped — keep presence flag active');
    return;
  }

  console.log('[PresenceService] Stopping presence session');
  
  this.isActive = false;
  this.isPaused = false;
  this._sessionStartPlayed = false;
  this._trackCount = 0;
  this._tracksSinceTransition = 0;
  this._recentTransitionIds = [];
  this._transitionLock = false;
    
    // Clear voice timer
    if (this.voiceTimer) {
      clearTimeout(this.voiceTimer);
      this.voiceTimer = null;
    }
    
    // Stop current voice if playing
    if (this.voiceSource) {
      try {
        this.voiceSource.stop();
        this.voiceSource.disconnect();
      } catch (e) {
        // Already stopped
      }
      this.voiceSource = null;
    }
    
    // Restore music gain
    if (this.musicGainNode) {
      this.restoreMusicGain();
    }
    
    this.isVoicePlaying = false;
    this._transitionLock = false;
    this.notifyStateChange('stopped');
  }
  
  /**
   * Pause presence session (stops voice scheduler but doesn't reset)
   */
  pause() {
    if (!this.isActive) return;
    
    console.log('[PresenceService] Pausing presence session');
    
    this.isPaused = true;
    
    // Clear voice timer
    if (this.voiceTimer) {
      clearTimeout(this.voiceTimer);
      this.voiceTimer = null;
    }
    
    // Stop current voice
    if (this.voiceSource) {
      try {
        this.voiceSource.stop();
      } catch (e) {}
      this.voiceSource = null;
      this.isVoicePlaying = false;
    }
    
    // Restore music gain
    this.restoreMusicGain();
  }
  
  /**
   * Resume presence session
   */
  resume() {
    if (!this.isActive || !this.isPaused) return;
    
    console.log('[PresenceService] Resuming presence session');
    
    this.isPaused = false;
    
    // V1.4.0: Periodic during_session messages DISABLED.
    // this.scheduleNextVoice();
  }
  
  /**
   * Schedule next voice message
   */
  scheduleNextVoice() {
    if (!this.isActive || this.isPaused) return;
    
    // Clear existing timer
    if (this.voiceTimer) {
      clearTimeout(this.voiceTimer);
    }
    
    console.log(`[PresenceService] Scheduling next voice in ${this.voiceInterval/1000}s`);
    
    this.voiceTimer = setTimeout(() => {
      this.playNextVoice();
    }, this.voiceInterval);
  }
  
  /**
   * Play next voice message via /api/voice-messages/pick (during_session).
   * Backend applies voice profile cooldown + probability.
   */
  async playNextVoice() {
    if (!this.isActive || this.isPaused) {
      console.log('[PresenceService] Not active or paused, skipping voice');
      return;
    }
    
    // HEAREN P0 — stale voice watchdog
    if (this.isVoicePlaying && this.voiceStartedAt) {
      const elapsed = Date.now() - this.voiceStartedAt;

      if (elapsed > 45000) {
        console.warn('[Transition] STALE VOICE DETECTED', {
          elapsed
        });

        this.resetVoiceState('transition watchdog');
      }
    }

    // Skip if voice already playing
    if (this.isVoicePlaying) {
      console.log('[Transition] Voice already playing, skipping');
      return false;
    }
    
    try {
      const voice = 'hera';
      const theme = this.theme || 'generic';
      const userId = this.userId || 'anonymous';
      
      const params = new URLSearchParams({
        trigger_mode: 'during_session',
        theme, lang: this.lang, voice, user_id: userId
      });

      if (this._lastPlayedMessageId) {
        params.set('last_message_id', this._lastPlayedMessageId);
      }
      
      console.log(`[PresenceService] Picking transition for theme=${theme}`);
      
      const response = await fetch(`${API_BASE_URL}/api/voice-messages/pick?${params}`);
      
      if (response.status === 204) {
        console.log('[PresenceService] No message picked (204)');
        this.scheduleNextVoice();
        return;
      }
      
      if (!response.ok) {
        console.warn(`[PresenceService] Pick error: HTTP ${response.status}`);
        this.scheduleNextVoice();
        return;
      }
      
      const data = await response.json();
      console.log(`[PresenceService] Picked: ${data.message_id} "${data.text?.substring(0, 40)}..."`);
      
      if (!data.file_url) {
        console.log('[PresenceService] No audio URL, skipping');
        this.scheduleNextVoice();
        return;
      }
      
      const streamUrl = data.file_url.includes('?')
        ? `${data.file_url}&stream=true`
        : `${data.file_url}?stream=true`;
      const msg = { id: data.message_id, text: data.text, audio_url: streamUrl };
      
      await this.playVoiceMessage(msg);
      this._lastPlayedMessageId = data.message_id;
      
      // Mark as played
      if (this.userId) {
        fetch(`${API_BASE_URL}/api/radio/mark-played?user_id=${this.userId}&message_id=${data.message_id}`, { method: 'POST' }).catch(() => {});
      }
      
    } catch (error) {
      console.error('[PresenceService] Error playing voice:', error);
    }
    
    // Schedule next
    this.scheduleNextVoice();
  }
  resetVoiceState(reason = 'unknown') {
  if (!this.isVoicePlaying && !this.voiceSource) {
    console.log('[PresenceService][RESET VOICE STATE] ignored — already clean', reason);
    return;
  }


  console.warn('[PresenceService][RESET VOICE STATE]', reason);

  try {
    if (this.voiceSource) {
      this.voiceSource.onended = null;

      try { this.voiceSource.stop(0); } catch (_) {}
      try { this.voiceSource.disconnect(); } catch (_) {}
    }
  } catch (e) {
    console.warn('[PresenceService][RESET ERROR]', e);
  }

  this.voiceSource = null;
  this.isVoicePlaying = false;
  this.voiceStartedAt = null;

  this.restoreMusicGain();
  this.notifyVoiceEnd();
}
  /**
   * Play a single voice message with music ducking
   */
  async playVoiceMessage(message) {
    // Guard: don't play if service was stopped
    if (!this.isActive) {
      console.log('[PresenceService] playVoiceMessage skipped — service not active');
      return;
    }

    if (!this.audioContext) {
      await this.initAudioContext();
    }
    
    // Resume context if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.isActive || this.isPaused) {
          console.log('[PresenceService] voice UI skipped — inactive or paused');
          resolve(false);
          return;
        }

        this.isVoicePlaying = true;
        this.voiceStartedAt = Date.now();
        this.notifyVoiceStart(message);
        
        // Build audio URL
        const audioUrl = message.audio_url || `/api/radio/voice-audio/${message.id}`;
        const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${API_BASE_URL}${audioUrl}`;
        
        console.log(`[PresenceService] Fetching voice audio: ${fullUrl}`);
        // HEAREN P0 — Try native Hera voice first for lockscreen/background support.
        // If native fails, continue to Web Audio fallback below.
        try {
          if (window.Capacitor?.isNativePlatform?.()) {
            const currentLang =
              this.language ||
              this.locale ||
              localStorage.getItem('hearen_lang') ||
              localStorage.getItem('hearen-locale') ||
              localStorage.getItem('hearen-language') ||
              '';

            const isEnglish = String(currentLang).toLowerCase().startsWith('en');

            const baseVoiceVolume = this.userVoiceVolume || 0.75;

            const nativeVoiceVolume = isEnglish
              ? Math.min(1.0, baseVoiceVolume * 1.7)
              : baseVoiceVolume;

            console.log('[PresenceService][VOLUME DEBUG]', {
              userVoiceVolume: this.userVoiceVolume,
              baseVoiceVolume,
              isEnglish,
              nativeVoiceVolume,
            });

            console.log('[PresenceService][NATIVE VOICE] trying native playVoice', {
              fullUrl,
              currentLang,
              isEnglish,
              voiceVolume: nativeVoiceVolume,
            });

            await nativeAudioPlayer.playVoice(
              fullUrl,
              nativeVoiceVolume,
              0.35
            );

            const estimatedDurationMs = Math.max(
              3000,
              Math.min(20000, (message.duration || 5) * 1000)
            );

            setTimeout(() => {
              console.log('[PresenceService][NATIVE VOICE] forced finished → resolve()');
              this.resetVoiceState('native forced finish');
              resolve();
            }, estimatedDurationMs);

            return;
          }
        } catch (nativeErr) {
          console.warn('[PresenceService][NATIVE VOICE] failed — falling back to Web Audio', {
            name: nativeErr?.name,
            message: nativeErr?.message,
          });
        }
        
        // Fetch and decode audio using SHARED AudioContext
        const response = await fetch(fullUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        console.log(`[PresenceService] Fetched ${arrayBuffer.byteLength} bytes`);
        
        if (arrayBuffer.byteLength === 0) {
          throw new Error('Empty audio response (possible CORS issue on iOS)');
        }
        
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        console.log(`[PresenceService] Decoded: ${audioBuffer.duration.toFixed(1)}s, ${audioBuffer.numberOfChannels}ch, ${audioBuffer.sampleRate}Hz`);
        
        // Re-check after async operations — user may have stopped during fetch
        if (!this.isActive) {
          console.log('[PresenceService] Service stopped during audio fetch — aborting playback');
          this.isVoicePlaying = false;
          this.notifyVoiceEnd();
          resolve();
          return;
        }
        
        const t = this.audioContext.currentTime;
        
        // DUCK MUSIC via musicBusGain
        this.duckMusic();
        
        // Create voice source and connect to SHARED voiceProgramGain
        // Chain: voiceSource → voiceProgramGain → voiceUserGain → masterGain → destination
        this.voiceSource = this.audioContext.createBufferSource();
        this.voiceSource.buffer = audioBuffer;
        this.voiceSource.connect(this.graph.voiceProgramGain);
        
        // V1.4.0: True fade-in — start voice immediately at gain=0, ramp up over 80ms
        this.graph.voiceProgramGain.gain.cancelScheduledValues(t);
        this.graph.voiceProgramGain.gain.setValueAtTime(1.0, t);
        
       // Handle voice end
       this.voiceSource.onended = () => {
        if (!this.isVoicePlaying) {
           console.log('[PresenceService] Voice ended ignored — already reset');
           return;
        }

        console.log('[PresenceService] Voice ended');
        this.resetVoiceState('onended');
        resolve();
      };
        
        console.log('[PresenceService][VOICE GRAPH CHECK BEFORE START]', {
          audioContextState: this.audioContext?.state,
          userVoiceVolume: this.userVoiceVolume,
          voiceBoost: this.voiceBoost,
          voiceUserGain: this.graph?.voiceUserGain?.gain?.value,
          voiceProgramGain: this.graph?.voiceProgramGain?.gain?.value,
          masterGain: this.graph?.masterGain?.gain?.value,
          bufferDuration: audioBuffer?.duration,
        });

        // Start voice NOW (fade-in masks any click)
        this.voiceSource.start(t);

        console.log('[PresenceService] Voice started (shared graph)');
        
      } catch (error) {
        console.error('[PresenceService] Error playing voice message:', {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          audioContextState: this.audioContext?.state,
        });

        this.resetVoiceState('catch');
        reject(error);
      }
    });
  }
  
  /**
   * Duck music volume via musicBusGain (smooth exponential curve)
   */
  duckMusic() {
    if (!this.graph) {
      console.warn('[PresenceService] No audio graph, cannot duck');
      return;
    }
    const t = this.audioContext.currentTime;
    const bus = this.graph.musicBusGain;
    // setTargetAtTime avoids clicks — smooth exponential approach
    // timeConstant 0.08 → reaches ~95% of target in 240ms
    bus.gain.cancelScheduledValues(t);
    bus.gain.setTargetAtTime(this.musicDuckLevel, t, 0.08);
    console.log(`[PresenceService] DUCK musicBusGain → ${this.musicDuckLevel} (exponential)`);
  }
  
  /**
   * Restore music volume via musicBusGain (smooth exponential curve)
   */
  restoreMusicGain() {
    if (!this.graph) return;
    const t = this.audioContext.currentTime;
    const bus = this.graph.musicBusGain;
    bus.gain.cancelScheduledValues(t);
    bus.gain.setTargetAtTime(1.0, t, 0.1);
    console.log(`[PresenceService] UNDUCK musicBusGain → 1.0 (exponential)`);
  }
  
  /**
   * Set callbacks
   */
  setCallbacks({ onVoiceStart, onVoiceEnd, onStateChange, onVolumeChange }) {
    if (onVoiceStart) this.onVoiceStart = onVoiceStart;
    if (onVoiceEnd) this.onVoiceEnd = onVoiceEnd;
    if (onStateChange) this.onStateChange = onStateChange;
    if (onVolumeChange) this.onVolumeChange = onVolumeChange;
  }
  
  /**
   * Notify voice start
   */
  notifyVoiceStart(message) {
    if (this.onVoiceStart) {
      this.onVoiceStart(message);
    }
  }
  
  /**
   * Notify voice end
   */
  notifyVoiceEnd() {
    if (this.onVoiceEnd) {
      this.onVoiceEnd();
    }
  }
  
  /**
   * Notify state change
   */
  notifyStateChange(state) {
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }
  
  /**
   * Get current state
   */
  getState() {
    return {
      isActive: this.isActive,
      isPaused: this.isPaused,
      isVoicePlaying: this.isVoicePlaying,
      voiceCount: this.voiceMessages.length,
      voiceIndex: this.voiceIndex,
    };
  }
  
  /**
   * Cleanup
   */
  destroy() {
    this.stop();
    
    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch (e) {}
      this.audioContext = null;
    }
    
    this.musicGainNode = null;
    this.voiceGainNode = null;
    this.masterGainNode = null;
  }
}

// Singleton instance
const presenceService = new PresenceService();

export default presenceService;
export { PresenceService };
